import { useState } from 'react';
import type { TarefaViva } from '@remoto/protocolo';
import { IconeAgente, IconeFechar } from '../icones';
import type { ItemDeChat } from '../../sessoes/mensagens';
import { Bolha } from './pecas';

type Subagente = Extract<ItemDeChat, { tipo: 'subagente' }>;

/**
 * A tela onde o trabalho dos subagentes acontece.
 *
 * Um subagente é uma sessão paralela: ele lê dezenas de arquivos, roda
 * comandos e devolve um relatório de centenas de linhas. Tudo isso cabia
 * dentro de um `<details>` no meio da conversa — dobrado, mas ali. Dobrado não
 * é o mesmo que fora do caminho: quem abria para conferir um passo perdia o
 * lugar da conversa, e quem não abria carregava o peso do mesmo jeito, porque
 * o cartão continuava competindo por atenção a cada rolagem.
 *
 * Aqui é o oposto: a conversa fica com o recibo de uma linha, e este painel
 * desliza por cima dela quando você quiser ver. Ele **desliza sobre a
 * conversa** em vez de virar uma aba do editor porque o celular precisa do
 * mesmo caminho — e no celular não existe área de edição.
 */
export function PainelDeAgentes({
  subagentes,
  tarefas,
  selecionado,
  aoSelecionar,
  aoFechar,
  aoPararTarefa,
  aoAbrirArquivo,
  cwd,
}: {
  subagentes: Subagente[];
  tarefas?: TarefaViva[];
  /** A chave do subagente aberto; vazio abre o último. */
  selecionado: string;
  aoSelecionar: (chave: string) => void;
  aoFechar: () => void;
  aoPararTarefa?: (tarefaId: string) => void;
  aoAbrirArquivo?: (caminho: string) => void;
  cwd?: string;
}): React.JSX.Element {
  const [aba, setAba] = useState<'passos' | 'resultado' | 'ordem'>('passos');

  const atual =
    subagentes.find((s) => s.chave === selecionado) ?? (subagentes[subagentes.length - 1] as
      | Subagente
      | undefined);
  const tarefa = tarefas?.find((t) => t.toolUseId && t.toolUseId === atual?.toolUseId);
  const rodando = (atual?.aberto ?? false) && (tarefa?.estado ?? 'rodando') === 'rodando';

  return (
    <div
      // `absolute inset-0` dentro da área da conversa, e não `fixed`: no
      // computador a conversa é só uma coluna do workspace, e um painel preso à
      // janela cobriria também o editor e a árvore, que não têm nada com isto.
      className="absolute inset-0 z-20 flex flex-col bg-zinc-950/95 backdrop-blur-sm"
      role="dialog"
      aria-label="Subagentes desta sessão"
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-zinc-800 px-3 py-2">
        <span className="text-violet-300" aria-hidden>
          <IconeAgente tamanho={16} />
        </span>
        <h2 className="min-w-0 flex-1 truncate text-sm text-zinc-200">
          Subagentes ({subagentes.length})
        </h2>
        <button
          type="button"
          onClick={aoFechar}
          aria-label="Fechar os agentes"
          title="Voltar para a conversa"
          className="rounded p-1 text-zinc-400 transition hover:bg-zinc-800 hover:text-zinc-100 pointer-coarse:min-h-11 pointer-coarse:min-w-11 pointer-coarse:flex pointer-coarse:items-center pointer-coarse:justify-center"
        >
          <IconeFechar tamanho={16} />
        </button>
      </div>

      {/*
        A lista é uma faixa que rola de lado, não uma coluna: no telefone uma
        coluna de agentes comeria metade da largura, e são poucos itens.
      */}
      {subagentes.length > 1 ? (
        <div className="flex shrink-0 gap-1.5 overflow-x-auto border-b border-zinc-900 px-3 py-2">
          {subagentes.map((s) => {
            const dele = tarefas?.find((t) => t.toolUseId && t.toolUseId === s.toolUseId);
            const vivo = s.aberto && (dele?.estado ?? 'rodando') === 'rodando';
            return (
              <button
                key={s.chave}
                type="button"
                onClick={() => aoSelecionar(s.chave)}
                className={
                  'flex shrink-0 items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition ' +
                  (s.chave === atual?.chave
                    ? 'border-violet-700 bg-violet-950/40 text-violet-100'
                    : 'border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200')
                }
              >
                {vivo ? (
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-violet-400" />
                ) : null}
                <span className="max-w-40 truncate">{s.subtipo ?? s.descricao}</span>
              </button>
            );
          })}
        </div>
      ) : null}

      {atual ? (
        <>
          <div className="shrink-0 border-b border-zinc-900 px-3 py-2">
            <p className="text-sm text-zinc-200">{atual.descricao}</p>
            <p className="mt-0.5 flex items-center gap-2 text-xs text-zinc-500">
              {atual.subtipo ? (
                <span className="rounded bg-violet-900/50 px-1 text-[10px] text-violet-300">
                  {atual.subtipo}
                </span>
              ) : null}
              <span>
                {atual.itens.length === 1 ? '1 passo' : `${atual.itens.length} passos`}
                {rodando ? ' · trabalhando' : ''}
              </span>
              {rodando && tarefa && aoPararTarefa ? (
                <button
                  type="button"
                  onClick={() => aoPararTarefa(tarefa.id)}
                  className="rounded border border-zinc-700 px-1.5 py-0.5 text-[11px] text-zinc-400 transition hover:border-red-700 hover:text-red-300"
                >
                  parar este subagente
                </button>
              ) : null}
            </p>
            {tarefa?.resumo ? (
              <p className="mt-1 border-l-2 border-violet-900/40 pl-2 text-xs text-zinc-400">
                {tarefa.resumo}
              </p>
            ) : null}
          </div>

          <div className="flex shrink-0 gap-1 border-b border-zinc-900 px-3 py-1.5 text-xs">
            {(
              [
                ['passos', 'Passos'],
                ['resultado', 'Resultado'],
                ['ordem', 'Ordem'],
              ] as const
            ).map(([id, rotulo]) => (
              <button
                key={id}
                type="button"
                onClick={() => setAba(id)}
                aria-pressed={aba === id}
                className={
                  'rounded px-2 py-1 transition ' +
                  (aba === id
                    ? 'bg-zinc-800 text-zinc-100'
                    : 'text-zinc-500 hover:bg-zinc-900 hover:text-zinc-300')
                }
              >
                {rotulo}
              </button>
            ))}
          </div>

          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
            {aba === 'passos' ? (
              atual.itens.length > 0 ? (
                atual.itens.map((interno) => (
                  <Bolha
                    key={interno.chave}
                    item={interno}
                    cwd={cwd ?? ''}
                    tarefas={tarefas}
                    {...(aoAbrirArquivo ? { aoAbrirArquivo } : {})}
                  />
                ))
              ) : (
                <p className="text-xs text-zinc-600">
                  {rodando ? 'Começando…' : 'Este subagente não registrou passos.'}
                </p>
              )
            ) : null}

            {aba === 'resultado' ? (
              atual.resultado ? (
                <pre
                  className={
                    'whitespace-pre-wrap break-words rounded bg-zinc-950/60 p-3 font-mono text-[11px] leading-relaxed ' +
                    (atual.resultadoComErro ? 'text-red-300' : 'text-zinc-300')
                  }
                >
                  {atual.resultado}
                </pre>
              ) : (
                <p className="text-xs text-zinc-600">
                  {rodando ? 'Ainda trabalhando.' : 'Nada foi devolvido.'}
                </p>
              )
            ) : null}

            {aba === 'ordem' ? (
              atual.prompt ? (
                <pre className="whitespace-pre-wrap break-words rounded bg-zinc-950/60 p-3 font-mono text-[11px] leading-relaxed text-zinc-400">
                  {atual.prompt}
                </pre>
              ) : (
                <p className="text-xs text-zinc-600">A ordem não veio no transcript.</p>
              )
            ) : null}
          </div>
        </>
      ) : (
        <p className="p-3 text-xs text-zinc-600">Nenhum subagente nesta conversa.</p>
      )}
    </div>
  );
}
